import { ReactNode, useEffect, useRef, useState } from "react";

import { Icon } from "./Icon";

/**
 * Desplegable de filtro: etiqueta, valor elegido y un popover con las opciones.
 *
 * Reemplaza a los `<select>` nativos. No es cosmético: el nativo no puede
 * mostrar el **conteo facetado** al lado de cada opción, y ese conteo es lo que
 * evita elegir un filtro que deja la lista vacía. Cada opción se cuenta con los
 * OTROS filtros aplicados menos el suyo; si se contara con el propio, elegir uno
 * dejaría a los demás en cero y la lista dejaría de decir nada.
 */
export interface OpcionMenu {
  valor: string;
  label: string;
  /** Cuántos quedarían si se elige esta opción. */
  n?: number;
}

export function Desplegable({
  etiqueta, valor, opciones, onElegir, ancho, activo, pie,
}: {
  etiqueta?: string;
  /** El valor elegido, ya en palabras. */
  valor: string;
  opciones: OpcionMenu[];
  onElegir: (valor: string) => void;
  ancho?: number;
  /** Si el filtro está puesto. Pinta el botón en verde. */
  activo?: boolean;
  /** Contenido extra al pie del popover — el calendario, por ejemplo. */
  pie?: ReactNode;
}) {
  const [abierto, setAbierto] = useState(false);
  const caja = useRef<HTMLDivElement | null>(null);

  // Cerrar al hacer clic afuera o con Escape. Sin esto quedan dos popovers
  // abiertos a la vez y el segundo tapa al primero.
  useEffect(() => {
    if (!abierto) return;
    const fuera = (e: MouseEvent) => {
      if (caja.current && !caja.current.contains(e.target as Node)) setAbierto(false);
    };
    const tecla = (e: KeyboardEvent) => { if (e.key === "Escape") setAbierto(false); };
    document.addEventListener("mousedown", fuera);
    document.addEventListener("keydown", tecla);
    return () => {
      document.removeEventListener("mousedown", fuera);
      document.removeEventListener("keydown", tecla);
    };
  }, [abierto]);

  return (
    <div className="desp" ref={caja}>
      <button className={"desp-btn" + (activo ? " on" : "")}
              aria-expanded={abierto}
              onClick={() => setAbierto((a) => !a)}>
        {etiqueta && <span className="desp-etiqueta">{etiqueta}</span>}
        <span className="desp-valor">{valor}</span>
        <span className="desp-chev"><Icon name="chevron" /></span>
      </button>

      {abierto && (
        <div className="desp-pop" style={ancho ? { width: ancho } : undefined}>
          {opciones.map((o) => (
            <button key={o.valor} className="desp-op"
                    onClick={() => { onElegir(o.valor); setAbierto(false); }}>
              <span className="desp-check">
                {o.valor === valorDe(opciones, valor) && <Icon name="check" />}
              </span>
              <span className="desp-label">{o.label}</span>
              {o.n != null && <span className="tabular desp-n">{o.n}</span>}
            </button>
          ))}
          {pie && <div className="desp-pie">{pie}</div>}
        </div>
      )}
    </div>
  );
}

/** El botón muestra el label; la marca ✓ se compara contra el valor. */
function valorDe(opciones: OpcionMenu[], label: string) {
  return opciones.find((o) => o.label === label)?.valor ?? label;
}
